import React from "react";

import { get, isEmpty, map, uniq } from "lodash/fp";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";

import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Stack, Typography } from "@mui/material";

import ActionButton from "@app/analytics/account/components/ActionButton";
import WizardTitle from "@app/analytics/account/components/WizardTitle";
import { WizardFormProps } from "@app/analytics/account/WorkspaceSettings/WorkspaceWizard/types";
import TextField from "@app/analytics/components/TextField";
import { yupResolver } from "@app/utils/forms/yupResolver";

import Button from "@rui/Button";

type TeamInput = { name: string };

const FIELD_ARRAY_NAME = "teams";
const newTeamInput = (): TeamInput => ({ name: "" });

const teamsValidationSchema = yup.object().shape({
  teams: yup.array().of(
    yup.object().shape({
      name: yup.string(),
    })
  ),
});

const CreateTeamsStepForm = ({
  form: wizardForm,
  onNextStep,
}: WizardFormProps) => {
  const existingTeams = wizardForm.getValues("teams");

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
  } = useForm({
    resolver: yupResolver(teamsValidationSchema),
    defaultValues: {
      [FIELD_ARRAY_NAME]: isEmpty(existingTeams)
        ? [newTeamInput()]
        : existingTeams,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELD_ARRAY_NAME,
  });

  const addTeamInput = () => append(newTeamInput());
  const handleRemoveTeamInput = (index: number) => () => remove(index);

  const validateUniqueTeamNames = (teams: TeamInput[]) => {
    const teamNames = map("name", teams);
    if (teamNames.length !== uniq(teamNames).length) {
      teamNames.forEach((name, i) => {
        if (teamNames.indexOf(name) !== i) {
          setError(`${FIELD_ARRAY_NAME}.${i}.name`, {
            type: "custom",
            message: "Team already exists",
          });
        }
      });
      return false;
    }
    return true;
  };

  const handleSubmitTeams = () => {
    const teams = getValues("teams").filter((team: TeamInput) =>
      Boolean(team.name)
    );

    const allTeamsUnique = validateUniqueTeamNames(teams);

    if (allTeamsUnique) {
      const submittedTeams = teams.map((team: TeamInput) => ({
        name: team.name,
      }));

      wizardForm.setValue("teams", submittedTeams);
      onNextStep();
    }
  };

  const handleSkip = () => onNextStep();

  return (
    <form onSubmit={handleSubmit(handleSubmitTeams)}>
      <Stack direction="column" spacing={3} sx={{ maxWidth: "600px" }}>
        {fields.map((field, i) => {
          const textFieldName = `${FIELD_ARRAY_NAME}.${i}.name` as const;
          const errorMessage = get(`${textFieldName}.message`, errors);
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              key={i}
            >
              <TextField
                required
                error={Boolean(errorMessage)}
                helperText={errorMessage}
                onEnter={addTeamInput}
                InputProps={{
                  ...register(textFieldName),
                  placeholder: 'Team name, like "Marketing"',
                  sx: { py: 0.5 },
                }}
                sx={{ maxWidth: "500px" }}
              />
              {i > 0 ? (
                <IconButton
                  onClick={handleRemoveTeamInput(i)}
                  sx={{
                    width: "48px",
                    height: "48px",
                    ml: 1,
                    mb: "auto",
                    color: "shades.primary.500",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              ) : (
                <Box sx={{ width: "48px", height: "48px" }} />
              )}
            </Stack>
          );
        })}
        <ActionButton
          sx={{ width: "fit-content" }}
          icon={<AddCircleOutlineOutlinedIcon sx={{ width: "18px" }} />}
          onClick={addTeamInput}
        >
          <Typography variant="button" sx={{ ml: 1 }}>
            Add another team
          </Typography>
        </ActionButton>
        <Stack direction="row" spacing={2}>
          <Button
            type="submit"
            onClick={handleSubmitTeams}
            sx={{ width: "160px" }}
          >
            Next Step
          </Button>
          <Button variant="text" onClick={handleSkip}>
            Skip for now
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};

const CreateTeamsStep = ({ onNextStep, form }: WizardFormProps) => (
  <Stack direction="column" spacing={3}>
    <WizardTitle
      title="Create teams in your organization"
      subtitle="Teams allow your organization to customize settings for different groups of people"
    />
    <CreateTeamsStepForm form={form} onNextStep={onNextStep} />
  </Stack>
);

export default CreateTeamsStep;
